import React, { LegacyRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DateTime, Info } from "luxon";
import { cls } from "app/utils";
// Reuse the CoachNow datepicker popup styling (calendar grid + round-chevron
// header) so this field matches the rest of the app (CFTB-3271).
import dpStyles from "app/components/DatePicker/styles.module.scss";
import styles from "./styles.module.scss";

interface Props {
  /** ISO date (yyyy-mm-dd), or "" when empty. */
  value: string;
  onChange: (iso: string) => void;
  placeholder?: string;
  id?: string;
}

// A date of birth is always in the past; cap the future and allow a sensible
// historical floor.
const MIN_DOB = new Date(1900, 0, 1);

// Defined at module scope so its component identity is stable across renders —
// react-datepicker clones this element, and a stable type keeps the input from
// remounting (and losing focus) on every keystroke while the user types.
const DobInput = React.forwardRef(
  (
    { open, value, onClick, placeholder, ...rest }: any,
    ref: LegacyRef<HTMLInputElement>,
  ) => (
    <div className={styles.dob_field} onClick={onClick}>
      <i className={cls("ico ico-calendar", styles.dob_icon_left)} />
      <input
        {...rest}
        ref={ref}
        type="text"
        className={styles.dob_input}
        value={value ?? ""}
        placeholder={placeholder}
      />
      <i
        className={cls(
          "ico ico-chevron",
          styles.dob_chevron,
          open && styles.dob_chevron_open,
        )}
      />
    </div>
  ),
);
DobInput.displayName = "DobInput";

const MONTH_NAMES = Info.months("long");

// Month + year dropdowns so a DOB (often years back) is reachable without
// scrolling month by month. Year range runs from this year back to MIN_DOB.
const DobHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
}: any) => {
  const current = DateTime.fromJSDate(date);
  const currentYear = new Date().getFullYear();
  const years: number[] = [];
  for (let y = currentYear; y >= MIN_DOB.getFullYear(); y -= 1) {
    years.push(y);
  }

  return (
    <div className={dpStyles.header}>
      <div
        className={dpStyles.date_arrow}
        style={{ transform: "rotate(90deg)" }}
        onClick={decreaseMonth}
      >
        <span>
          <i className="ico ico-chevron" />
        </span>
      </div>
      <div className={styles.dob_header_selects}>
        <select
          value={current.month - 1}
          onChange={(e) => changeMonth(Number(e.target.value))}
        >
          {MONTH_NAMES.map((name, index) => (
            <option key={name} value={index}>
              {name}
            </option>
          ))}
        </select>
        <select
          value={current.year}
          onChange={(e) => changeYear(Number(e.target.value))}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>
      <div
        className={dpStyles.date_arrow}
        style={{ transform: "rotate(-90deg)" }}
        onClick={increaseMonth}
      >
        <span>
          <i className="ico ico-chevron" />
        </span>
      </div>
    </div>
  );
};

const DateOfBirthField = ({ value, onChange, placeholder, id }: Props) => {
  const [open, setOpen] = useState(false);
  const selected = value ? DateTime.fromISO(value).toJSDate() : null;

  return (
    <div className={cls(dpStyles.date_picker, styles.dob_wrapper)}>
      <ReactDatePicker
        id={id}
        selected={selected}
        onChange={(d) =>
          onChange(d ? DateTime.fromJSDate(d).toISODate() ?? "" : "")
        }
        onCalendarOpen={() => setOpen(true)}
        onCalendarClose={() => setOpen(false)}
        customInput={<DobInput open={open} />}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
        }) => (
          <DobHeader
            date={date}
            changeYear={changeYear}
            changeMonth={changeMonth}
            decreaseMonth={decreaseMonth}
            increaseMonth={increaseMonth}
          />
        )}
        dateFormat="d MMM, yyyy"
        placeholderText={placeholder}
        maxDate={new Date()}
        minDate={MIN_DOB}
        calendarClassName={dpStyles.day_picker}
        useWeekdaysShort
      />
    </div>
  );
};

export default DateOfBirthField;
